import { useStore } from '../composables/store'
import Mango from './mango'

/**
 * Validates inventory levels for items in the cart
 * @returns {Object} Object containing out of stock items and low stock items
 */
export async function validateInventory() {
	const store = useStore()
	const oosItems = []
	const lowStockItems = []

	console.log('🔍 validateInventory - Starting cart validation')
	// console.log('🛒 Initial cart items:', JSON.parse(JSON.stringify(store.cart.cartItems)))

	// Skip validation if cart is empty
	if (!store.cart?.cartItems?.length) {
		console.log('⚠️ Cart is empty, skipping validation')
		return { oosItems, lowStockItems }
	}

	// Collect all product IDs from cart items
	const productIds = store.cart.cartItems.filter((item) => item.item?.bcId).map((item) => item.item.bcId)
	// console.log('📋 Product IDs to check:', productIds)

	// Collect all variant IDs from cart items
	const variantIds = store.cart.cartItems.filter((item) => item.variant?.bcId).map((item) => item.variant.bcId)
	// console.log('📋 Variant IDs to check:', variantIds)

	// Skip validation if no products or variants to check
	if (!productIds.length && !variantIds.length) {
		console.log('⚠️ No product or variant IDs found, skipping validation')
		return { oosItems, lowStockItems }
	}

	// Build search queries for products and variants
	const productSearch = productIds.length ? { bcId: { $in: productIds } } : null
	const variantSearch = variantIds.length ? { bcId: { $in: variantIds } } : null
	// console.log('🔎 Product search query:', productSearch)
	// console.log('🔎 Variant search query:', variantSearch)

	// Fetch products and variants from database
	// console.log('📡 Fetching products and variants from database...')
	const [products, variants] = await Promise.all([
		productIds.length ? Mango.products({ search: productSearch, fields: ['bcId', 'name', 'inventoryLevel', 'inventoryTracking', 'outOfStock'] }) : [],
		variantIds.length ? Mango.variants({ search: variantSearch, fields: ['bcId', 'name', 'inventoryLevel'] }) : [],
	])
	// console.log(`✅ Fetched ${products.length} products and ${variants.length} variants`)

	// Create lookup maps for faster access
	const productMap = products.reduce((map, product) => {
		map[product.bcId] = product
		return map
	}, {})

	const variantMap = variants.reduce((map, variant) => {
		map[variant.bcId] = variant
		return map
	}, {})

	// console.log(
	// 	'📚 Product inventory map:',
	// 	Object.entries(productMap).map(([bcId, product]) => ({
	// 		bcId,
	// 		name: product.name,
	// 		inventoryLevel: product.inventoryLevel,
	// 		inventoryTracking: product.inventoryTracking,
	// 		outOfStock: product.outOfStock,
	// 	})),
	// )
	// console.log(
	// 	'📚 Variant inventory map:',
	// 	Object.entries(variantMap).map(([bcId, variant]) => ({
	// 		bcId,
	// 		name: variant.name,
	// 		inventoryLevel: variant.inventoryLevel,
	// 	})),
	// )

	// Validate each cart item
	// console.log('🔍 Starting validation of each cart item...')
	for (const item of store.cart.cartItems) {
		// console.log('🔎 Validating item:', JSON.stringify(item))

		// Always use the database values for inventory validation
		if (item?.item?.inventoryTracking === 'product') {
			// Fallback to product lookup if direct inventory check isn't available
			const product = productMap[item?.item?.bcId]

			if (!product) {
				console.log(`⚠️ Product ${item?.item?.bcId} not found in database, skipping validation`)
				continue
			}

			// console.log('📦 Product Map Product:', product)

			if (item.quantity > product.inventoryLevel) {
				if (product.inventoryLevel <= 0 || product.outOfStock) {
					// Remove from cart
					console.log('🚫 REMOVING item from cart - Out of stock')
					const originalLength = store.cart.cartItems.length

					// Remove the item based on its unique identifier
					if (item.variant?.bcId) {
						// If it has a variant, remove by variant ID
						store.cart.cartItems = store.cart.cartItems.filter((i) => i.variant?.bcId !== item.variant.bcId)
					} else if (item.item?.bcId) {
						// Otherwise remove by item ID
						store.cart.cartItems = store.cart.cartItems.filter((i) => i.item?.bcId !== item.item.bcId)
					} else {
						// If no bcId, try to match by item id
						store.cart.cartItems = store.cart.cartItems.filter((i) => i.item?.id !== item.item?.id)
					}

					// console.log(`✖️ Removed ${originalLength - store.cart.cartItems.length} items from cart`)
					oosItems.push(item)
				} else {
					// Update quantity to available inventory
					const originalQty = item.quantity
					console.log(`⚠️ UPDATING item quantity from ${originalQty} to ${product.inventoryLevel} - Low stock`)
					item.quantity = product.inventoryLevel
					// Create a copy of the item with originalQty added
					const itemWithOriginalQty = { ...item, originalQty }
					lowStockItems.push(itemWithOriginalQty)
				}
			} else {
				// console.log('✅ Item has sufficient inventory')
			}
		} else if (item?.item?.inventoryTracking === 'variant') {
			// Fallback to variant lookup if direct inventory check isn't available
			const variant = variantMap[item.variant.bcId]

			if (!variant) {
				console.log(`⚠️ Variant ${item.variant.bcId} not found in database, skipping validation`)
				continue
			}

			// console.log('📊 Variant inventory check (fallback):', {
			// 	itemQuantity: item.quantity,
			// 	variantInventoryLevel: variant.inventoryLevel,
			// 	outOfStock: variant.outOfStock,
			// })

			if (item.quantity > variant.inventoryLevel) {
				if (variant.inventoryLevel <= 0 || variant.outOfStock) {
					// Remove from cart
					console.log('🚫 REMOVING item from cart - Out of stock')
					const originalLength = store.cart.cartItems.length

					// For variants, we can directly filter by variant bcId
					store.cart.cartItems = store.cart.cartItems.filter((i) => i.variant?.bcId !== item.variant.bcId)

					// console.log(`✖️ Removed ${originalLength - store.cart.cartItems.length} items from cart`)
					oosItems.push(item)
				} else {
					// Update quantity to available inventory
					const originalQty = item.quantity
					console.log(`⚠️ UPDATING item quantity from ${originalQty} to ${variant.inventoryLevel} - Low stock`)
					item.quantity = variant.inventoryLevel
					// Create a copy of the item with originalQty added
					const itemWithOriginalQty = { ...item, originalQty }
					lowStockItems.push(itemWithOriginalQty)
				}
			} else {
				// console.log('✅ Item has sufficient inventory')
			}
		} else {
			// console.log('ℹ️ Item does not have inventory tracking or missing ID, skipping validation', item?.item)
		}
	}

	// Update cart in local storage
	window.localStorage.setItem('cart', JSON.stringify(store.cart))

	// Log validation results
	console.log('\n📋 Validation Results Summary:')
	console.log(
		`🚫 Out of stock items removed: ${oosItems.length}`,
		oosItems.length
			? oosItems.map((item) => ({
					name: item.item?.name || item.variant?.name || 'Unknown',
					productId: item.item?.bcId,
					variantId: item.variant?.bcId,
				}))
			: '',
	)
	console.log(
		`⚠️ Low stock items updated: ${lowStockItems.length}`,
		lowStockItems.length
			? lowStockItems.map((item) => ({
					name: item.item?.name || item.variant?.name || 'Unknown',
					productId: item.item?.bcId,
					variantId: item.variant?.bcId,
					newQuantity: item.quantity,
				}))
			: '',
	)
	console.log('🛒 Final cart items count:', store.cart.cartItems.length)

	return { oosItems, lowStockItems }
}
